import React, { useMemo, useState } from 'react'

function matchesCountrySearch(country, query) {
  const q = query.trim().toLowerCase()
  if (!q) return true

  if (country.country.toLowerCase().includes(q)) return true
  if (country.iso2.toLowerCase().includes(q)) return true
  if (country.calling_code.toLowerCase().includes(q)) return true

  const queryDigits = q.replace(/\D/g, '')
  if (queryDigits) {
    const codeDigits = country.calling_code.replace(/\D/g, '')
    return codeDigits.startsWith(queryDigits)
  }

  return false
}

export default function CountryCodeSelect({ countries, value, onChange, style, className = 'form-control' }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)

  const selectedCountry = useMemo(
    () => countries.find((country) => country.iso2 === value) || countries[0] || null,
    [countries, value]
  )

  const filteredCountries = useMemo(
    () => countries.filter((country) => matchesCountrySearch(country, searchTerm)),
    [countries, searchTerm]
  )

  const closeDropdown = () => {
    setShowDropdown(false)
    setSearchTerm('')
    setHighlightIndex(-1)
  }

  const handleSelect = (country) => {
    if (!country) return
    onChange(country.iso2, country)
    closeDropdown()
  }

  const handleFocus = () => {
    setShowDropdown(true)
    setSearchTerm('')
    setHighlightIndex(-1)
  }

  const handleBlur = () => {
    setTimeout(closeDropdown, 200)
  }

  const handleChange = (e) => {
    setSearchTerm(e.target.value)
    setShowDropdown(true)
    setHighlightIndex(-1)
  }

  const handleKeyDown = (e) => {
    if (!showDropdown) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setShowDropdown(true)
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((prev) =>
        prev < filteredCountries.length - 1 ? prev + 1 : prev
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightIndex >= 0 && filteredCountries[highlightIndex]) {
        handleSelect(filteredCountries[highlightIndex])
      } else if (filteredCountries.length === 1) {
        handleSelect(filteredCountries[0])
      }
    } else if (e.key === 'Escape') {
      closeDropdown()
    }
  }

  const displayValue = showDropdown
    ? searchTerm
    : selectedCountry
      ? `${selectedCountry.country} (${selectedCountry.calling_code})`
      : ''

  const listboxId = 'country-code-listbox'

  return (
    <div style={{ position: 'relative', ...style }}>
      <input
        type="text"
        className={className}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={showDropdown ? 'Type country or +code' : 'Select country'}
        autoComplete="off"
        role="combobox"
        aria-label="Country code"
        aria-expanded={showDropdown}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-autocomplete="list"
      />

      {showDropdown && filteredCountries.length > 0 && (
        <div
          id={listboxId}
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            minWidth: '280px',
            maxHeight: '220px',
            overflowY: 'auto',
            backgroundColor: '#fff',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            zIndex: 1050,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginTop: '2px'
          }}
        >
          {filteredCountries.map((country, index) => (
            <div
              key={country.iso2}
              role="option"
              aria-selected={country.iso2 === value}
              onMouseDown={(e) => {
                e.preventDefault()
                handleSelect(country)
              }}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: index === highlightIndex ? '#e9ecef' : '#fff',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={() => setHighlightIndex(index)}
            >
              {country.country} ({country.calling_code})
            </div>
          ))}
        </div>
      )}

      {showDropdown && searchTerm && filteredCountries.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            minWidth: '280px',
            padding: '8px 12px',
            backgroundColor: '#fff',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            zIndex: 1050,
            color: '#6c757d',
            marginTop: '2px'
          }}
        >
          No country found
        </div>
      )}
    </div>
  )
}
